import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import './GridInput.css';
import DateInput from './DateInput';
import api from '../api/axiosConfig';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { format, isValid } from 'date-fns';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import DualScrollTable from '../components/DualScrollTable';
import CustomerModal from '../components/CustomerModal';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─────────────────────────────────────────────
// 유틸 함수 (컴포넌트 외부 → 재생성 없음)
// ─────────────────────────────────────────────
const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') return '';
  const number = parseFloat(num.toString().replace(/,/g, ''));
  return isNaN(number) ? '' : number.toLocaleString();
};

const unformatNumber = (str) => String(str || '').replace(/,/g, '');
const parseNumber = (v) => {
  if (v === null || v === undefined || v === '') return NaN;
  const n = Number(String(v).replace(/,/g, ''));
  return isNaN(n) ? NaN : n;
};
const parseDate = (v) => {
  if (!v) return NaN;
  if (v instanceof Date) return v.getTime();
  const t = Date.parse(v);
  return isNaN(t) ? NaN : t;
};
const normalizeForSave = (val) => {
  if (val instanceof Date) return val.toISOString().split('T')[0];
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.split('T')[0];
  if (!isNaN(val)) return String(val);
  return val;
};
// 숫자만 추출 (엑셀용)
const parseNumberForExcel = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const number = parseFloat(num.toString().replace(/,/g, ''));
  return isNaN(number) ? 0 : number;
};

// ─────────────────────────────────────────────
// 컬럼별 고정 너비 매핑 (thStyle에서 공통 사용)
// colgroup의 width 값과 반드시 동일하게 유지할 것
// ─────────────────────────────────────────────
const COLUMN_WIDTHS = {
  FIELD_NAME: 200,
  ISSUED_DATE: 110,
  CUSTOMER_NAME: 250,
  IN_OUT: 100,
  ETC_YN: 70,
  DIVISION: 150,
  PROVIDE_AMOUNT: 110,
  VAT: 110,
  TOTAL_AMOUNT: 110,
  PAID_DATE: 110,
  PAID_AMOUNT: 110,
  REMAIN_AMOUNT: 110,
  REMARK: 200,
  CREATED_AT: 130,
  UPDATED_AT: 130,
};

// ─────────────────────────────────────────────
// Read/Edit 분리 셀 - 객체 옵션용 (현장, 거래처)
// 클릭 전: 텍스트 div (DOM 최소화)
// 클릭 후: Autocomplete 마운트
// ─────────────────────────────────────────────
const EditableSelectCell = memo(({ value, options, onChange, getLabel, isEqual, placeholder, sx }) => {
  const [editing, setEditing] = useState(false);

  const displayLabel = useMemo(() => {
    if (!value) return '';
    const found = options.find(o => isEqual(o, value));
    return found ? getLabel(found) : '';
  }, [value, options, getLabel, isEqual]);

  const handleChange = useCallback((_, v) => {
    onChange(v);
    setEditing(false);
  }, [onChange]);

  if (!editing) {
    return (
      <div
        onClick={() => setEditing(true)}
        style={{
          cursor: 'pointer',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: displayLabel ? '#1e293b' : '#94a3b8',
          userSelect: 'none',
        }}
      >
        {displayLabel || placeholder || '선택'}
      </div>
    );
  }

  return (
    <Autocomplete
      autoFocus
      open
      options={options}
      value={value || null}
      onChange={handleChange}
      onBlur={() => setEditing(false)}
      getOptionLabel={getLabel}
      isOptionEqualToValue={isEqual}
      sx={{ width: '100%', ...sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          placeholder={placeholder}
          variant="standard"
          InputProps={{ ...params.InputProps, disableUnderline: true }}
          sx={{ px: 1 }}
        />
      )}
    />
  );
});
EditableSelectCell.displayName = 'EditableSelectCell';

// ─────────────────────────────────────────────
// Read/Edit 분리 셀 - string 배열용 (매입/매출, 구분)
// ─────────────────────────────────────────────
const EditableStringCell = memo(({ value, options, onChange, placeholder, width }) => {
  const [editing, setEditing] = useState(false);

  const handleChange = useCallback((_, v) => {
    onChange(v || '');
    setEditing(false);
  }, [onChange]);

  if (!editing) {
    return (
      <div
        onClick={() => setEditing(true)}
        style={{
          cursor: 'pointer',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: value ? '#1e293b' : '#94a3b8',
          userSelect: 'none',
        }}
      >
        {value || <span style={{ color: '#94a3b8' }}>{placeholder || '선택'}</span>}
      </div>
    );
  }

  return (
    <Autocomplete
      autoFocus
      open
      options={options}
      value={value || null}
      onChange={handleChange}
      onBlur={() => setEditing(false)}
      sx={{ width: width || 80 }}
      disableClearable
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          placeholder={placeholder}
          variant="standard"
          InputProps={{ ...params.InputProps, disableUnderline: true }}
          sx={{ px: 1 }}
        />
      )}
    />
  );
});
EditableStringCell.displayName = 'EditableStringCell';

// ─────────────────────────────────────────────
// 행 컴포넌트 (React.memo)
// props가 실제로 바뀐 행만 리렌더링
// ─────────────────────────────────────────────
const GridRow = memo(({ row, idx, fieldOptions, customerOptions, onRowChange, onCheckChange }) => {
    const remainAmt   = parseNumber(row.REMAIN_AMOUNT);
    const remainClass = !isNaN(remainAmt) && remainAmt !== 0 ? 'amount-remain' : '';

  const handleFieldChange    = useCallback((v) => onRowChange(row.COST_ID, '__FIELD__',    v), [row.COST_ID, onRowChange]);
  const handleCustomerChange = useCallback((v) => onRowChange(row.COST_ID, '__CUSTOMER__', v), [row.COST_ID, onRowChange]);
  const handleInOutChange    = useCallback((v) => onRowChange(row.COST_ID, 'IN_OUT',       v), [row.COST_ID, onRowChange]);
  const handleDivisionChange = useCallback((v) => onRowChange(row.COST_ID, 'DIVISION',     v), [row.COST_ID, onRowChange]);
  const handleChange         = useCallback((field, value) => onRowChange(row.COST_ID, field, value), [row.COST_ID, onRowChange]);

  const fieldValue    = useMemo(() => fieldOptions.find(o => o.FIELD_ID === Number(row.FIELD_ID)) || null,       [fieldOptions, row.FIELD_ID]);
  const customerValue = useMemo(() => customerOptions.find(o => o.CUSTOMER_ID === Number(row.CUSTOMER_ID)) || null, [customerOptions, row.CUSTOMER_ID]);

  // 안정 참조 - useCallback으로 선언해야 EditableSelectCell의 useMemo가 제대로 동작
  const getFieldLabel    = useCallback((o) => o?.FIELD_NAME    || '', []);
  const isFieldEqual     = useCallback((o, v) => o.FIELD_ID    === v.FIELD_ID, []);
  const getCustomerLabel = useCallback((o) => o?.CUSTOMER_NAME || '', []);
  const isCustomerEqual  = useCallback((o, v) => o.CUSTOMER_ID === v.CUSTOMER_ID, []);

  return (
    <tr style={{ backgroundColor: row._new ? '#eff6ff' : 'transparent' }}>

      {/* 체크박스 */}
      <td>
        <input
          type="checkbox"
          checked={row._checked || false}
          onChange={() => onCheckChange(row.COST_ID)}
          className="small-checkbox"
        />
      </td>

      {/* No */}
      <td>{idx + 1}</td>

      {/* 현장명 */}
      <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
        <EditableSelectCell
          value={fieldValue}
          options={fieldOptions}
          onChange={handleFieldChange}
          getLabel={getFieldLabel}
          isEqual={isFieldEqual}
          placeholder="현장 선택"
          sx={{ width: 200 }}
        />
      </td>

      {/* 발행일자 */}
      <td>
        <DateInput value={row.ISSUED_DATE} onChange={(val) => handleChange('ISSUED_DATE', val)} locale={ko} portalId="portal-root" />
      </td>

      {/* 거래처명 */}
      <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
        <EditableSelectCell
          value={customerValue}
          options={customerOptions}
          onChange={handleCustomerChange}
          getLabel={getCustomerLabel}
          isEqual={isCustomerEqual}
          placeholder="거래처 선택"
          sx={{ width: 250 }}
        />
      </td>

      {/* 매입/매출 */}
      <td>
        <EditableStringCell
          value={row.IN_OUT}
          options={['매입', '매출']}
          onChange={handleInOutChange}
          placeholder="선택"
          width={100}
        />
      </td>

      {/* 기타경비 */}
      <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
        <input
          type="checkbox"
          checked={row.ETC_YN === 'Y'}
          onChange={(e) => handleChange('ETC_YN', e.target.checked ? 'Y' : 'N')}
        />
      </td>

      {/* 구분 */}
      <td>
        <EditableStringCell
          value={row.DIVISION}
          options={['장비', '자재외', '']}
          onChange={handleDivisionChange}
          placeholder="구분 선택"
          width={150}
        />
      </td>

      {/* 공급가 */}
      <td><input className="right-align" value={formatNumber(row.PROVIDE_AMOUNT)} onChange={(e) => handleChange('PROVIDE_AMOUNT', unformatNumber(e.target.value))} /></td>

      {/* 부가세 */}
      <td><input className="right-align" value={formatNumber(row.VAT)} onChange={(e) => handleChange('VAT', unformatNumber(e.target.value))} /></td>

      {/* 합계금액 */}
      <td><input className="right-align" value={formatNumber(row.TOTAL_AMOUNT)} onChange={(e) => handleChange('TOTAL_AMOUNT', unformatNumber(e.target.value))} /></td>

      {/* 결제일자 */}
      <td>
        <DateInput value={row.PAID_DATE} onChange={(val) => handleChange('PAID_DATE', val)} locale={ko} portalId="portal-root" />
      </td>

      {/* 결제금액 */}
      <td><input className="right-align" value={formatNumber(row.PAID_AMOUNT)} onChange={(e) => handleChange('PAID_AMOUNT', unformatNumber(e.target.value))} /></td>

    {/* 잔액 (읽기전용) */}
    <td className={remainClass}>
      <input className="right-align" value={formatNumber(row.REMAIN_AMOUNT)} readOnly />
    </td>

      {/* 비고 */}
      <td><input style={{ textAlign: 'left' }} value={row.REMARK || ''} onChange={(e) => handleChange('REMARK', e.target.value)} /></td>

      {/* 등록일 */}
      <td className="readonly-col"><span>{row.CREATED_AT}</span></td>
      {/* 수정일 */}
      <td className="readonly-col"><span>{row.UPDATED_AT}</span></td>
    </tr>
  );
});
GridRow.displayName = 'GridRow';


// ─────────────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────────────
const GridInput = () => {
  const [rows, setRows]                   = useState([]);
  const [selectAll, setSelectAll]         = useState(false);
  const [fieldOptions, setFieldOptions]   = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [searchDate, setSearchDate]       = useState(new Date());
  const [sortKey, setSortKey]             = useState('');
  const [sortDir, setSortDir]             = useState('asc');
  const [fieldId, setFieldId]             = useState('');
  const [customerId, setCustomerId]       = useState('');
  const [modalOpen, setModalOpen]         = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // ── state 추가 ──────────────────────────────
  const [dateType, setDateType] = useState('ISSUED_DATE');

  const checkedCount = useMemo(() => rows.filter(r => r._checked).length, [rows]);
  const hasNewRows   = useMemo(() => rows.some(r => r._new), [rows]);

  //매입매출 조회조건
  const [inOut, setInOut] = useState('');
  //기타경비여부 조회조건
  const [etcYn, setEtcYn] = useState('');

  // ── 데이터 조회 ──────────────────────────────
  const fetchData = useCallback((date, fId, cId, inOut, etcYn, dType) => {
    const params = [];

    if (date && isValid(date)) {
      if (dType === 'CREATED_AT') {
        params.push(`ymd=${format(date, 'yyyy-MM-dd')}`);  // 일조회
      } else {
        params.push(`ym=${format(date, 'yyyy-MM')}`);      // 월조회
      }
    }

    if (fId != null)   params.push(`fieldId=${fId}`);
    if (cId != null)   params.push(`customerId=${cId}`);
    if (inOut != null) params.push(`inOut=${inOut}`);
    if (etcYn != null) params.push(`etcYn=${etcYn}`);
    if (dType)         params.push(`dateType=${dType}`);

    const url = `/api/${params.length > 0 ? `?${params.join('&')}` : ''}`;
    api.get(url)
      .then(res => setRows((res.data || []).map(r => ({ ...r, _original: { ...r } }))))
      .catch(console.error);
  }, []);

  useEffect(() => {
    api.get(`/api/fieldList`)
      .then(res => setFieldOptions(
        (res.data || []).filter((item, i, self) => i === self.findIndex(t => t.FIELD_ID === item.FIELD_ID))
      ))
      .catch(console.error);
    api.get(`/api/customer/searchList`)
      .then(res => setCustomerOptions(
        (res.data || []).filter((item, i, self) => i === self.findIndex(t => t.CUSTOMER_ID === item.CUSTOMER_ID))
      ))
      .catch(console.error);
  }, []);

  // ── dateType 전환 시 searchDate 초기화 핸들러 ──
  const handleDateTypeChange = useCallback((value) => {
    setDateType(value);
    setSearchDate(new Date()); // 모드 바뀌면 날짜 오늘로 리셋
  }, []);

  useEffect(() => {
    fetchData(searchDate, fieldId, customerId, inOut, etcYn, dateType);
  }, [searchDate, fieldId, customerId, inOut, etcYn, dateType, fetchData]);

  // ── 행 변경 핸들러 (useCallback으로 참조 안정화) ──
  const handleRowChange = useCallback((id, field, value) => {
    setRows(prev => prev.map(row => {
      if (row.COST_ID !== id) return row;

      if (field === '__FIELD__') {
        return { ...row, FIELD_ID: value ? value.FIELD_ID : null, FIELD_NAME: value ? value.FIELD_NAME : '' };
      }

      if (field === '__CUSTOMER__') {
        const updated = { ...row, CUSTOMER_ID: value ? value.CUSTOMER_ID : '' };
        if (updated.IN_OUT !== '매출') {
          updated.IN_OUT         = value?.IN_OUT         || '';
          updated.ETC_YN         = value?.ETC_YN         || 'N';
          updated.DIVISION       = value?.DIVISION       || '';
        } else {
          updated.IN_OUT = ''; updated.ETC_YN = 'N'; updated.DIVISION = '';
        }
        return updated;
      }

      const updatedRow = { ...row, [field]: value };

      if (field === 'PROVIDE_AMOUNT') {
        const supply = parseFloat(unformatNumber(value) || 0);
        const vat = Math.round(supply * 0.1);
        updatedRow.VAT = String(vat);
        updatedRow.TOTAL_AMOUNT = String(supply + vat);
      }
      if (field === 'VAT') {
        const supply = parseFloat(unformatNumber(updatedRow.PROVIDE_AMOUNT) || 0);
        const vat    = parseFloat(unformatNumber(value) || 0);
        updatedRow.TOTAL_AMOUNT = String(supply + vat);
      }
      if (field === 'TOTAL_AMOUNT' || field === 'PAID_AMOUNT') {
        const total = parseFloat(unformatNumber(updatedRow.TOTAL_AMOUNT) || 0);
        const paid  = parseFloat(unformatNumber(updatedRow.PAID_AMOUNT)  || 0);
        updatedRow.REMAIN_AMOUNT = String(total - paid);
      }

      // 결제일자 선택 시 결제금액 자동 세팅
      if (field === 'PAID_DATE' && value) {
        const total = parseFloat(unformatNumber(updatedRow.TOTAL_AMOUNT) || 0);
        updatedRow.PAID_AMOUNT   = String(total);
        updatedRow.REMAIN_AMOUNT = 0;
      }

      // 결제일자 지우면 결제금액도 초기화
      if (field === 'PAID_DATE' && !value) {
        updatedRow.PAID_AMOUNT   = 0;
        updatedRow.REMAIN_AMOUNT = updatedRow.TOTAL_AMOUNT || 0;
      }

      return updatedRow;
    }));
  }, []);

  const handleCheckChange = useCallback((id) => {
    setRows(prev => prev.map(r => r.COST_ID === id ? { ...r, _checked: !r._checked } : r));
  }, []);

    //행추가
    const addRow = useCallback(() => {
      // 조회조건에 현장이 선택돼 있으면 그 현장으로 채움
      const selectedField = fieldId ? fieldOptions.find(o => o.FIELD_ID === fieldId) : null;

      setRows(prev => ([{
        COST_ID: `new_${Date.now()}`,
        FIELD_ID:   selectedField ? selectedField.FIELD_ID   : '',
        FIELD_NAME: selectedField ? selectedField.FIELD_NAME : '',
        ISSUED_DATE: searchDate, CUSTOMER_ID: '', IN_OUT: '',
        PROVIDE_AMOUNT: '', VAT: '', TOTAL_AMOUNT: '', PAID_DATE: null,
        PAID_AMOUNT: null, REMAIN_AMOUNT: null, DIVISION: '', ETC_YN: 'N', REMARK: null, _new: true,
      }, ...prev]));
    }, [searchDate, fieldId, fieldOptions]);


  // ── 저장 ─────────────────────────────────────
  const handleSave = useCallback(() => {
    if (!window.confirm('저장하시겠습니까?')) return;
    const required = ['FIELD_NAME', 'ISSUED_DATE', 'CUSTOMER_ID', 'IN_OUT', 'PROVIDE_AMOUNT', 'VAT', 'TOTAL_AMOUNT'];
    const labels   = { FIELD_NAME: '현장명', ISSUED_DATE: '발행일자', CUSTOMER_ID: '거래처명', IN_OUT: '매입/매출', PROVIDE_AMOUNT: '공급가', VAT: '부가세', TOTAL_AMOUNT: '합계금액' };
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      for (const k of required) {
        if (r[k] === null || r[k] === undefined || r[k] === '') {
          alert(`필수값을 입력해주세요 (행 ${i + 1}, 항목: ${labels[k] || k})`);
          return;
        }
      }
    }
    const keys = ['FIELD_ID', 'FIELD_NAME', 'ISSUED_DATE', 'CUSTOMER_ID', 'IN_OUT', 'ETC_YN',
      'PROVIDE_AMOUNT', 'VAT', 'TOTAL_AMOUNT', 'PAID_DATE', 'PAID_AMOUNT', 'REMAIN_AMOUNT', 'DIVISION', 'REMARK'];
    const createdList = rows.filter(r => r._new)
      .map(r => { const { _original, _checked, _new, ...c } = r; return c; })
      .reverse();
    const updatedList = rows.filter(r => !r._new && r._original)
      .filter(r => keys.some(k => normalizeForSave(r[k]) !== normalizeForSave(r._original[k])))
      .map(r => { const { _original, _checked, _new, ...c } = r; return c; });
    if (!createdList.length && !updatedList.length) { alert('변경된 내용이 없습니다.'); return; }
    api.post(`/api/save`, { createdList, updatedList })
      .then(() => { alert('저장되었습니다.'); fetchData(searchDate, fieldId, customerId, inOut, etcYn,dateType); })
      .catch(err => {
        if (err.response?.status === 409) alert(err.response.data);
        else alert('저장 중 오류가 발생했습니다.\n' + err);
      });
  }, [rows, searchDate, fieldId, customerId, inOut, etcYn, fetchData]);

  // ── 삭제 ─────────────────────────────────────
  const handleDelete = useCallback(() => {
    if (!window.confirm('선택한 항목을 삭제하시겠습니까?')) return;
    const willDelete = rows.filter(r => r._checked && !r._new);
    if (willDelete.length) {
      api.post(`/api/delete`, willDelete)
        .then(() => { alert('삭제되었습니다.'); setRows(prev => prev.filter(r => !r._checked)); })
        .catch(console.error);
    } else {
      setRows(prev => prev.filter(r => !r._checked));
    }
  }, [rows]);

  // ── 팝업 저장 ────────────────────────────────
  const handlePopupSave = useCallback((formData) => {
    api.post(`/api/customer/save`, { createdList: [formData], updatedList: [] })
      .then(() => {
        alert('저장되었습니다.');
        api.get(`/api/customer/searchList`)
          .then(res => setCustomerOptions(
            (res.data || []).filter((item, i, self) => i === self.findIndex(t => t.CUSTOMER_ID === item.CUSTOMER_ID))
          ))
          .catch(console.error);
      })
      .catch(err => alert('저장 중 오류가 발생했습니다. ' + (err.response?.data || err.message)));
    setModalOpen(false);
  }, []);

  const handleAddClick = useCallback(() => { setSelectedCustomer(null); setModalOpen(true); }, []);

  // ── 정렬 ─────────────────────────────────────
  const cycleSort = useCallback((key) => {
    if (sortKey !== key) {
      // 다른 컬럼 클릭 → 오름차순 시작
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      // 같은 컬럼 1번 클릭 → 내림차순
      setSortDir('desc');
    } else {
      // 같은 컬럼 2번 클릭 → 정렬 해제
      setSortKey('');
      setSortDir('asc');
    }
  }, [sortKey, sortDir]);

  const sortIcon = (key) => {
    if (sortKey !== key) return '↕';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  const comparator = useCallback((a, b, key) => {
    if (['PROVIDE_AMOUNT', 'VAT', 'TOTAL_AMOUNT', 'PAID_AMOUNT', 'REMAIN_AMOUNT'].includes(key)) {
      const na = parseNumber(a[key]); const nb = parseNumber(b[key]);
      if (isNaN(na) && isNaN(nb)) return 0; if (isNaN(na)) return -1; if (isNaN(nb)) return 1;
      return na - nb;
    }
    if (['ISSUED_DATE', 'PAID_DATE', 'CREATED_AT', 'UPDATED_AT'].includes(key)) {
      const da = parseDate(a[key]); const db = parseDate(b[key]);
      if (isNaN(da) && isNaN(db)) return 0; if (isNaN(da)) return -1; if (isNaN(db)) return 1;
      return da - db;
    }
    if (key === 'CUSTOMER_NAME') {
      const sa = (customerOptions.find(c => Number(c.CUSTOMER_ID) === Number(a.CUSTOMER_ID))?.CUSTOMER_NAME || '').toLowerCase();
      const sb = (customerOptions.find(c => Number(c.CUSTOMER_ID) === Number(b.CUSTOMER_ID))?.CUSTOMER_NAME || '').toLowerCase();
      if (sa < sb) return -1; if (sa > sb) return 1; return 0;
    }
    const sa = (a[key] ?? '').toString().toLowerCase();
    const sb = (b[key] ?? '').toString().toLowerCase();
    if (sa < sb) return -1; if (sa > sb) return 1; return 0;
  }, [customerOptions]);

  const sortedRows = useMemo(() => {
    if (hasNewRows || !sortKey) return rows;
    return [...rows].sort((a, b) => {
      const cmp = comparator(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir, hasNewRows, comparator]);

  const totals = useMemo(() => {
    return sortedRows.reduce((acc, row) => ({
      PROVIDE_AMOUNT: (acc.PROVIDE_AMOUNT || 0) + (parseFloat(row.PROVIDE_AMOUNT) || 0),
      VAT:            (acc.VAT            || 0) + (parseFloat(row.VAT)            || 0),
      TOTAL_AMOUNT:   (acc.TOTAL_AMOUNT   || 0) + (parseFloat(row.TOTAL_AMOUNT)   || 0),
      PAID_AMOUNT:    (acc.PAID_AMOUNT    || 0) + (parseFloat(row.PAID_AMOUNT)    || 0),
      REMAIN_AMOUNT:  (acc.REMAIN_AMOUNT  || 0) + (parseFloat(row.REMAIN_AMOUNT)  || 0),
    }), {});
  }, [sortedRows]);

  const handleTableKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    let currentCell = e.target.closest('td');
    while (currentCell) {
      const nextCell = currentCell.nextElementSibling;
      if (nextCell) {
        const nextFocusable = nextCell.querySelector('input, button, [tabindex]:not([tabindex="-1"])');
        if (nextFocusable) { nextFocusable.focus(); return; }
        currentCell = nextCell;
      } else { break; }
    }
  }, []);

  // 컬럼별 고정 너비를 자동으로 포함하는 헤더 스타일 헬퍼
    const thStyle = (key) => ({
      cursor: hasNewRows ? 'not-allowed' : 'pointer',
      color: hasNewRows ? '#94a3b8' : undefined,   // 투명도 대신 흐린 글자색
      width: COLUMN_WIDTHS[key],
    });

  const exportExcel = useCallback(async () => {
    if (sortedRows.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('매출매입');

      // 열 너비
      worksheet.columns = [
        { width: 5  },  // No
        { width: 20 },  // 현장명
        { width: 13 },  // 발행일자
        { width: 22 },  // 거래처명
        { width: 10 },  // 매입/매출
        { width: 10 },  // 기타경비
        { width: 10 },  // 구분
        { width: 15 },  // 공급가
        { width: 13 },  // 부가세
        { width: 15 },  // 합계금액
        { width: 13 },  // 결제일자
        { width: 15 },  // 결제금액
        { width: 15 },  // 잔액
        { width: 22 },  // 비고
      ];

      const NUM_FMT  = '#,##0';
      const FONT     = { name: '굴림체', size: 10 };
      const BORDER   = {
        top: { style: 'thin' }, left:  { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' },
      };
      const NUM_COLS  = [8, 9, 10, 12, 13]; // 공급가, 부가세, 합계금액, 결제금액, 잔액
      const LEFT_COLS = [2, 4, 14];          // 현장명, 거래처명, 비고

      // 셀 스타일 일괄 적용 헬퍼
      const styleRow = (row, bgColor = null, bold = false, isHeader = false) => {
        row.eachCell({ includeEmpty: true }, (cell, colNum) => {
          cell.font   = { ...FONT, bold };
          cell.border = BORDER;

          let horizontal;
          if (isHeader) {
            horizontal = 'center';                // 헤더 전부 가운데
          } else if (LEFT_COLS.includes(colNum)) {
            horizontal = 'left';                  // 현장명, 거래처명, 비고 왼쪽
          } else if (NUM_COLS.includes(colNum)) {
            horizontal = 'right';                 // 숫자 오른쪽
          } else {
            horizontal = 'center';                // 나머지 가운데
          }

          cell.alignment = { horizontal, vertical: 'middle' };
          if (NUM_COLS.includes(colNum)) cell.numFmt = NUM_FMT;
          if (bgColor) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        });
        row.height = 18;
      };

      // ── 헤더 행 ──────────────────────────────────────
      const headerRow = worksheet.addRow([
        'No', '현장명', '발행일자', '거래처명', '매입/매출',
        '기타경비', '구분', '공급가', '부가세', '합계금액',
        '결제일자', '결제금액', '잔액', '비고',
      ]);
      styleRow(headerRow, 'FFD9E1F2', true, true);
      headerRow.height = 20;

      // ── 데이터 행 ─────────────────────────────────────
      sortedRows.forEach((row, idx) => {
        const fieldName    = fieldOptions.find(o => o.FIELD_ID    === Number(row.FIELD_ID))?.FIELD_NAME    || '';
        const customerName = customerOptions.find(o => o.CUSTOMER_ID === Number(row.CUSTOMER_ID))?.CUSTOMER_NAME || '';

        const dataRow = worksheet.addRow([
          idx + 1,
          fieldName,
          row.ISSUED_DATE ? String(row.ISSUED_DATE).split('T')[0] : '',
          customerName,
          row.IN_OUT   || '',
          row.ETC_YN === 'Y' ? '기타경비' : '세금계산서',
          row.DIVISION || '',
          parseNumberForExcel(row.PROVIDE_AMOUNT),
          parseNumberForExcel(row.VAT),
          parseNumberForExcel(row.TOTAL_AMOUNT),
          row.PAID_DATE ? String(row.PAID_DATE).split('T')[0] : '',
          parseNumberForExcel(row.PAID_AMOUNT),
          parseNumberForExcel(row.REMAIN_AMOUNT),
          row.REMARK || '',
        ]);

        // 잔액 < 0 이면 연한 빨강 강조
        const bgColor = parseNumberForExcel(row.REMAIN_AMOUNT) < 0 ? 'FFFFCCCC' : null;
        styleRow(dataRow, bgColor);
      });

      // ── 합계 행 ──────────────────────────────────────
      const totalRow = worksheet.addRow([
        '합 계', '', '', '', '', '', '',
        parseNumberForExcel(totals.PROVIDE_AMOUNT),
        parseNumberForExcel(totals.VAT),
        parseNumberForExcel(totals.TOTAL_AMOUNT),
        '',
        parseNumberForExcel(totals.PAID_AMOUNT),
        parseNumberForExcel(totals.REMAIN_AMOUNT),
        '',
      ]);
      // 1~7열 병합
      worksheet.mergeCells(totalRow.number, 1, totalRow.number, 7);
      styleRow(totalRow, 'FFEEF1F4', true);
      totalRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

      // ── 저장 ─────────────────────────────────────────
      const buffer = await workbook.xlsx.writeBuffer();
      const blob   = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `매출매입_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`);

    } catch (err) {
      console.error('엑셀 내보내기 오류:', err);
      alert('엑셀 내보내기 중 오류가 발생했습니다.');
    }
  }, [sortedRows, fieldOptions, customerOptions, totals]);

  return (
    <div className="grid-container">
      <h2>매출/매입 등록</h2>

      {/* 조회 조건 */}
      <div className="gh-band">
        <div className="gh-band__row">
          <div className="gh-band__title">
            <span className="gh-octo" aria-hidden>▶</span>
            <span>조회조건</span>
            <span className="gh-badge">조회결과: {rows.length}</span>
          </div>
        </div>
        <div className="gh-band__filters">
          <div className="filter-item gh-field">
            {/* 토글 버튼 */}
            <div style={{ display: 'flex', borderRadius: 3, overflow: 'hidden', border: '1px solid #bbb', marginRight: 6 }}>
              {[{ label: '발행일', value: 'ISSUED_DATE' }, { label: '등록일', value: 'CREATED_AT' }].map(({ label, value }) => (
                <button
                  key={value}
                  onClick={() => handleDateTypeChange(value)}
                  style={{
                    padding: '2px 10px',
                    fontSize: 12,
                    flex: 1,
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: dateType === value ? '#1e6fbf' : '#f0f0f0',
                    color: dateType === value ? '#fff' : '#333',
                    fontWeight: dateType === value ? 600 : 400,
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* DatePicker - dateType에 따라 모드 전환 */}
            {dateType === 'ISSUED_DATE' ? (
              <DatePicker
                id="monthPicker"
                selected={searchDate}
                onChange={(date) => setSearchDate(date)}
                dateFormat="yyyy-MM"
                showMonthYearPicker              // 월 선택
                placeholderText="년월 선택"
                locale={ko}
                className="gh-input"
                portalId="portal-root"
                popperPlacement="bottom-start"
              />
            ) : (
              <DatePicker
                id="dayPicker"
                selected={searchDate}
                onChange={(date) => setSearchDate(date)}
                dateFormat="yyyy-MM-dd"          // 일 선택
                placeholderText="날짜 선택"
                locale={ko}
                className="gh-input"
                portalId="portal-root"
                popperPlacement="bottom-start"
              />
            )}
          </div>
          <div className="filter-item gh-field">
            <label>현장</label>
            <Autocomplete
              options={fieldOptions}
              value={fieldOptions.find(o => o.FIELD_ID === fieldId) || null}
              onChange={(_, v) => setFieldId(v ? v.FIELD_ID : '')}
              getOptionLabel={(o) => o?.FIELD_NAME || ''}
              isOptionEqualToValue={(o, v) => o.FIELD_ID === v.FIELD_ID}
              renderInput={(params) => (
                <TextField {...params} placeholder="현장 선택" variant="standard"
                  InputProps={{ ...params.InputProps, disableUnderline: true }} sx={{ px: 1, minWidth: 200 }} />
              )}
            />
          </div>

          <div className="filter-item gh-field">
            <label>거래처</label>
            <Autocomplete
              options={customerOptions}
              value={customerOptions.find(o => o.CUSTOMER_ID === customerId) || null}
              onChange={(_, v) => setCustomerId(v ? v.CUSTOMER_ID : '')}
              getOptionLabel={(o) => o?.CUSTOMER_NAME || ''}
              isOptionEqualToValue={(o, v) => o.CUSTOMER_ID === v.CUSTOMER_ID}
              renderInput={(params) => (
                <TextField {...params} placeholder="거래처 선택" variant="standard"
                  InputProps={{ ...params.InputProps, disableUnderline: true }} sx={{ px: 1, minWidth: 200 }} />
              )}
            />
          </div>
          <div className="filter-item gh-field">
            <label>매출매입</label>
            <Autocomplete
              options={['매출', '매입']}
              value={inOut}                          // ex) useState('')
              onChange={(_, v) => setInOut(v || '')}
              renderInput={(params) => (
                <TextField {...params} placeholder="매출매입 선택" variant="standard"
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                  sx={{ px: 1, minWidth: 200 }} />
              )}
            />
          </div>

          <div className="filter-item gh-field">
            <label>경비구분</label>
            <RadioGroup
              row
              value={etcYn}
              onChange={(e) => setEtcYn(e.target.value)}
              sx={{ px: 1 }}
            >
              <FormControlLabel value="" control={<Radio size="small" />} label="전체"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }} />
              <FormControlLabel value="Y" control={<Radio size="small" />} label="기타경비"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }} />
              <FormControlLabel value="N" control={<Radio size="small" />} label="세금계산서"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }} />
            </RadioGroup>
          </div>

          <div className="filter-actions gh-actions">
            <button className="btn" onClick={() => fetchData(searchDate, fieldId, customerId, inOut, etcYn, dateType)}>조회</button>
          </div>
        </div>
      </div>

      {/* 버튼 그룹 */}
      <div className="button-group-right">
        <button onClick={handleAddClick} className="btn btn-ghost">거래처 추가</button>
        <button onClick={addRow} className="btn">행 추가</button>
        <button onClick={exportExcel} className="btn btn-ghost">엑셀 내려받기</button>
        <button onClick={handleSave} className="btn btn-primary">저장</button>
        <button onClick={handleDelete} className="btn btn-danger" disabled={checkedCount === 0}>삭제 ({checkedCount})</button>
      </div>

      {/* 그리드 */}
      <DualScrollTable>
        <table className="gi-grid-table gi-grid-table-with-fixed-columns" onKeyDown={handleTableKeyDown}>
          <colgroup>
            <col style={{ width: 40 }} />    {/* 체크박스 */}
            <col style={{ width: 55 }} />    {/* No */}
            <col style={{ width: 200 }} />   {/* 현장명 */}
            <col style={{ width: 110 }} />   {/* 발행일자 */}
            <col style={{ width: 250 }} />   {/* 거래처명 */}
            <col style={{ width: 100 }} />   {/* 매입/매출 */}
            <col style={{ width: 70 }} />    {/* 기타경비 */}
            <col style={{ width: 150 }} />   {/* 구분 */}
            <col style={{ width: 120 }} />   {/* 공급가 */}
            <col style={{ width: 120 }} />   {/* 부가세 */}
            <col style={{ width: 120 }} />   {/* 합계금액 */}
            <col style={{ width: 120 }} />   {/* 결제일자 */}
            <col style={{ width: 120 }} />   {/* 결제금액 */}
            <col style={{ width: 120 }} />   {/* 잔액 */}
            <col style={{ width: 200, minWidth: 150 }} />   {/* 비고 */}
            <col style={{ width: 150 }} />   {/* 등록일 */}
            <col style={{ width: 150 }} />   {/* 수정일 */}
          </colgroup>
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input type="checkbox" checked={selectAll}
                  onChange={(e) => {
                    setSelectAll(e.target.checked);
                    setRows(prev => prev.map(r => ({ ...r, _checked: e.target.checked })));
                  }} />
              </th>
              <th style={{ width: 55 }}>No</th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('FIELD_NAME')} style={thStyle('FIELD_NAME')} className="required-label">
                현장명 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('FIELD_NAME')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('ISSUED_DATE')} style={thStyle('ISSUED_DATE')} className="required-label">
                발행일자 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('ISSUED_DATE')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('CUSTOMER_NAME')} style={thStyle('CUSTOMER_NAME')} className="required-label">
                거래처명 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('CUSTOMER_NAME')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('IN_OUT')} style={thStyle('IN_OUT')} className="required-label">
                매입/매출 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('IN_OUT')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('ETC_YN')} style={thStyle('ETC_YN')}>
                기타경비 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('ETC_YN')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('DIVISION')} style={thStyle('DIVISION')}>
                구분 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('DIVISION')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('PROVIDE_AMOUNT')} style={thStyle('PROVIDE_AMOUNT')} className="required-label">
                공급가 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('PROVIDE_AMOUNT')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('VAT')} style={thStyle('VAT')} className="required-label">
                부가세 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('VAT')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('TOTAL_AMOUNT')} style={thStyle('TOTAL_AMOUNT')} className="required-label">
                합계금액 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('TOTAL_AMOUNT')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('PAID_DATE')} style={thStyle('PAID_DATE')}>
                결제일자 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('PAID_DATE')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('PAID_AMOUNT')} style={thStyle('PAID_AMOUNT')}>
                결제금액 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('PAID_AMOUNT')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('REMAIN_AMOUNT')} style={thStyle('REMAIN_AMOUNT')}>
                잔액 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('REMAIN_AMOUNT')}</span>
              </th>
              <th style={thStyle('REMARK')}>비고</th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('CREATED_AT')} style={thStyle('CREATED_AT')}>
                등록일시 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('CREATED_AT')}</span>
              </th>
              <th role="button" onClick={() => !hasNewRows && cycleSort('UPDATED_AT')} style={thStyle('UPDATED_AT')}>
                수정일시 <span style={{ fontSize: 12, color: '#64748b' }}>{sortIcon('UPDATED_AT')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr><td colSpan={16} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>조회된 내용이 없습니다.</td></tr>
            ) : (
              sortedRows.map((row, idx) => (
                <GridRow
                  key={row.COST_ID}
                  row={row}
                  idx={idx}
                  fieldOptions={fieldOptions}
                  customerOptions={customerOptions}
                  onRowChange={handleRowChange}
                  onCheckChange={handleCheckChange}
                />
              ))
            )}
          </tbody>

          <tfoot>
            <tr style={{ fontWeight: 700 }}>
              <td></td>                                         {/* 1 체크박스 */}
              <td></td>                                         {/* 2 No */}
              <td style={{ textAlign: 'center' }}>합 계</td>    {/* 3 현장명 */}
              <td></td>                                         {/* 4 발행일자 */}
              <td></td>                                         {/* 5 거래처명 */}
              <td></td>                                         {/* 6 매입/매출 */}
              <td></td>                                         {/* 7 기타경비 */}
              <td></td>                                         {/* 8 구분 */}
              <td><span className="right-align">{formatNumber(totals.PROVIDE_AMOUNT)}</span></td>  {/* 9 공급가 */}
              <td><span className="right-align">{formatNumber(totals.VAT)}</span></td>             {/* 10 부가세 */}
              <td><span className="right-align">{formatNumber(totals.TOTAL_AMOUNT)}</span></td>     {/* 11 합계금액 */}
              <td></td>                                         {/* 12 결제일자 */}
              <td><span className="right-align">{formatNumber(totals.PAID_AMOUNT)}</span></td>      {/* 13 결제금액 */}
              <td><span className="right-align">{formatNumber(totals.REMAIN_AMOUNT)}</span></td>    {/* 14 잔액 */}
              <td></td>                                         {/* 15 비고 */}
              <td></td>                                         {/* 16 등록일시 */}
              <td></td>                                         {/* 17 수정일시 */}
            </tr>
          </tfoot>

        </table>
      </DualScrollTable>

      <CustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handlePopupSave}
        initialData={selectedCustomer}
      />
    </div>
  );
};

export default GridInput;